/**
 * Auf welcher Ebene ein Wartungsende gepflegt wird — und wen es trifft.
 *
 * Das Wartungsende hängt entweder an der VERSION selbst oder wird von der
 * VERSIONSLINIE geerbt (`catalog.service.ts::loadEffectiveLifecycles`: eigenes
 * Ende schlägt das der Linie). In der Kundenakte sieht man beim Pflegen
 * zunächst nur das eine System — die Änderung wirkt aber auf jedes System mit
 * dieser Version bzw. mit dieser Linie. Diese Datei rechnet die betroffene
 * Menge aus, damit die Maske sie beziffern kann statt nur zu warnen.
 *
 * Reine Funktionen ohne React und ohne Netz — die Vererbungsfälle sind damit
 * ohne gerenderte Maske prüfbar (Muster: `softwareOptionsFilter.ts`).
 */
import type { VersionRef } from "../../hooks/useMaintenance.js";

export type LifecycleLevel = "version" | "line";

/**
 * Trägt diese Version ein EIGENES Wartungsende?
 *
 * `inheritedFromLine` allein reicht nicht: Es ist auch dann `false`, wenn es
 * überhaupt kein Ende gibt (der Server setzt es als Rückfallwert). Erst
 * zusammen mit `maintenanceEnd !== null` bedeutet es „eigenes Ende".
 */
export function hasOwnEnd(version: Pick<VersionRef, "maintenanceEnd" | "inheritedFromLine">) {
  return version.maintenanceEnd !== null && !version.inheritedFromLine;
}

export interface ResolvedScope {
  /** Die Version des Softwarestands, sofern die Katalogliste sie kennt. */
  version: VersionRef | null;
  /**
   * Die Versionslinie. `null`, wenn die Version selbst die Linie ist
   * (`parentVersionId === null`) — dann gibt es nur die eine Ebene.
   */
  line: VersionRef | null;
}

export function resolveScope(versions: VersionRef[], versionId: string): ResolvedScope {
  const version = versions.find((v) => v.id === versionId) ?? null;
  const parentId = version?.parentVersionId ?? null;
  return {
    version,
    line: parentId === null ? null : (versions.find((v) => v.id === parentId) ?? null),
  };
}

/**
 * Wie viele Systeme die Änderung auf der gewählten Ebene trifft.
 *
 * - Ebene `version`: die Systeme genau dieser Version.
 * - Ebene `line`: die Systeme der Linie SELBST plus die aller ihrer Versionen,
 *   die kein eigenes Ende tragen. Versionen mit eigenem Ende bleiben unberührt
 *   — ihr Ende schlägt das der Linie ohnehin.
 *
 * Die Linie zählt immer mit, auch wenn sie bereits ein eigenes Ende trägt: Sie
 * IST die Version, auf die geschrieben wird. Sie über dieselbe
 * `hasOwnEnd`-Bedingung wie ihre Kinder auszuschließen, war der erste Anlauf —
 * und zählte genau die Systeme nicht mit, die die Änderung am sichersten
 * trifft.
 */
export function affectedSystemCount(
  versions: VersionRef[],
  level: LifecycleLevel,
  versionId: string,
): number {
  const { version, line } = resolveScope(versions, versionId);
  if (level === "version") return version?.systemCount ?? 0;
  if (!line) return 0;
  return versions
    .filter((v) => v.id === line.id || (v.parentVersionId === line.id && !hasOwnEnd(v)))
    .reduce((sum, v) => sum + v.systemCount, 0);
}
